/**
 * The language before anyone signs in: picked on the sign-in page, else the
 * browser's. Remembered per browser (a convenience, so storage may be absent),
 * and kept in step with the signed-in user's choice, so signing out does not
 * switch languages.
 */
import { useSyncExternalStore } from 'react';

import { type Locale, matchLocale } from '@daka/core';

const STORAGE_KEY = 'daka.locale';
/** Set when the language was picked on the sign-in page: saved to the profile at sign-in (also across an SSO redirect). */
const PICKED_KEY = 'daka.locale.picked';
const listeners = new Set<() => void>();

function read(key: string): string | null {
   try {
      return localStorage.getItem(key);
   } catch {
      return null;
   }
}

function write(key: string, value: string | null): void {
   try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
   } catch {
      // Private mode or blocked storage: the choice lasts until reload.
   }
}

// A stored choice, else the browser's language — either matched onto the core `LOCALES`.
let current: Locale = matchLocale(read(STORAGE_KEY) ?? navigator.language);

export function setDeviceLocale(locale: Locale): void {
   if (locale === current) return;
   current = locale;
   write(STORAGE_KEY, locale);
   for (const listener of listeners) listener();
}

/** A choice on the sign-in page: shown at once, and saved to the profile at sign-in. */
export function pickDeviceLocale(locale: Locale): void {
   write(PICKED_KEY, locale);
   setDeviceLocale(locale);
}

/** The language picked before sign-in, if any — once. */
export function takePickedLocale(): Locale | null {
   const picked = read(PICKED_KEY);
   write(PICKED_KEY, null);
   return picked === null ? null : matchLocale(picked);
}

export function useDeviceLocale(): Locale {
   return useSyncExternalStore(
      (listener) => {
         listeners.add(listener);
         return () => listeners.delete(listener);
      },
      () => current
   );
}
